// Pannello di configurazione integrazione IoT MQTT
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '../../../components/ui/Button'
import { CredentialsNote } from './CredentialsNote'
import api from '../../../lib/api'

const schema = z.object({
  metricRetentionDays: z.coerce.number().int().min(1).max(365).default(30),
  alertThresholdTemperature: z.coerce.number().min(-100).max(1000).default(85),
  alertThresholdVibration: z.coerce.number().min(0).max(100).default(8.5),
  enablePredictiveMaintenance: z.boolean().default(true),
  heartbeatTimeoutSeconds: z.coerce.number().int().min(30).max(3600).default(300),
  qosLevel: z.coerce.number().int().min(0).max(2).default(1),
})

type FormValues = z.infer<typeof schema>

interface IoTConfigProps {
  currentMetadata?: Record<string, unknown>
  onSaved?: () => void
}

export function IoTConfig({ currentMetadata, onSaved }: IoTConfigProps) {
  const qc = useQueryClient()

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      metricRetentionDays: (currentMetadata?.['metricRetentionDays'] as number) ?? 30,
      alertThresholdTemperature: (currentMetadata?.['alertThresholdTemperature'] as number) ?? 85,
      alertThresholdVibration: (currentMetadata?.['alertThresholdVibration'] as number) ?? 8.5,
      enablePredictiveMaintenance: (currentMetadata?.['enablePredictiveMaintenance'] as boolean) ?? true,
      heartbeatTimeoutSeconds: (currentMetadata?.['heartbeatTimeoutSeconds'] as number) ?? 300,
      qosLevel: (currentMetadata?.['qosLevel'] as number) ?? 1,
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      await api.put('/integrations/iot/config', { metadata: data })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations'] })
      onSaved?.()
    },
  })

  return (
    <div className="space-y-5">
      <CredentialsNote envVars={['IOT_MQTT_BROKER_URL', 'IOT_MQTT_USERNAME', 'IOT_MQTT_PASSWORD']} />

      <div className="rounded-lg border border-[var(--fc-border)] p-3 bg-[var(--fc-surface)]">
        <p className="text-xs text-[var(--fc-foreground-muted)]">
          <span className="font-medium text-[var(--fc-foreground)]">Topic MQTT:</span>{' '}
          I device devono pubblicare su{' '}
          <span className="font-mono text-[var(--fc-primary)]">fieldcore/devices/{'{'}{'{'}deviceId{'}'}{'}'}/#</span>.
          KleisNote si sottoscrive automaticamente al momento della registrazione del device.
        </p>
      </div>

      <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
              Retention metriche (giorni)
            </label>
            <input
              type="number"
              min={1}
              max={365}
              className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
              {...register('metricRetentionDays')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
              QoS MQTT (0/1/2)
            </label>
            <select
              className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
              {...register('qosLevel')}
            >
              <option value={0}>0 — At most once (fire and forget)</option>
              <option value={1}>1 — At least once (acknowledged)</option>
              <option value={2}>2 — Exactly once (two-phase)</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
              Soglia alert temperatura (°C)
            </label>
            <input
              type="number"
              step="0.1"
              className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
              {...register('alertThresholdTemperature')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
              Soglia alert vibrazione (m/s²)
            </label>
            <input
              type="number"
              step="0.1"
              min={0}
              className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
              {...register('alertThresholdVibration')}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
            Timeout heartbeat device (secondi)
          </label>
          <input
            type="number"
            min={30}
            max={3600}
            className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
            {...register('heartbeatTimeoutSeconds')}
          />
          <p className="mt-1 text-xs text-[var(--fc-foreground-muted)]">
            Dopo quanti secondi senza heartbeat un device viene marcato come offline.
          </p>
        </div>

        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="enablePredictiveMaintenance"
            className="mt-0.5 h-4 w-4 rounded border-[var(--fc-border)]"
            {...register('enablePredictiveMaintenance')}
          />
          <div>
            <label htmlFor="enablePredictiveMaintenance" className="text-sm text-[var(--fc-foreground)]">
              Abilita manutenzione predittiva AI
            </label>
            <p className="text-xs text-[var(--fc-foreground-muted)]">
              Le metriche IoT vengono analizzate dal modulo AI (Cap 6) per predire guasti imminenti.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onSaved}>Annulla</Button>
          <Button type="submit" isLoading={saveMutation.isPending}>Salva configurazione</Button>
        </div>
      </form>
    </div>
  )
}
